import { ESTIMATED_PHOTO_BYTES } from '~/calculations/imageScaling'
import type { PhotoRecord, PhotoView } from '~/models/photo'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { putBlob } from '~/storage/blobStore'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { estimateStorage } from '~/storage/quota'
import {
  deleteAllPhotos,
  deletePhoto,
  listPhotos,
  putPhoto,
} from '~/storage/repositories/photoRepository'
import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toIsoTimestamp } from '~/types/dates'
import type { PhaseId, PhotoId, ProjectId } from '~/types/ids'
import { newId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { PhotoPipelineError } from './photoPipeline'
import { processCapturedImage } from './photoPipeline'
/* The app's own local calendar date (D-034), never an EXIF timestamp. Shared
   with weight entry so a photo and a weigh-in taken minutes apart can never
   land on different days. */
import { todayFor } from './weightService'

/**
 * Saving, listing and deleting progress photos (D-081).
 *
 * The pipeline turns a file into bytes; this turns bytes into a record the app
 * owns, and answers the quota question before either happens.
 */

export type SavePhotoError =
  | { readonly kind: 'pipeline'; readonly error: PhotoPipelineError }
  | { readonly kind: 'storage'; readonly error: StorageError }
  | { readonly kind: 'noRoom'; readonly message: string }

export interface SavePhotoInput {
  readonly file: Blob
  readonly view: PhotoView
  readonly source: PhotoRecord['source']
  readonly now: Date
  readonly phaseId?: PhaseId
  readonly projectId?: ProjectId
  readonly note?: string
}

/**
 * Quota headroom, expressed as what the app should do about it (D-081).
 *
 * `unknown` is a real and common answer, not a failure: Safari does not always
 * provide an estimate, and this project's rule is that an absent value is never
 * treated as a good one. An unknown estimate lets the capture proceed and
 * relies on the typed `QuotaExceededError` from the write, because refusing
 * every capture on a browser that declines to answer would break the feature
 * outright for those users.
 */
export type QuotaVerdict =
  | { readonly state: 'ok' }
  | { readonly state: 'unknown' }
  | { readonly state: 'low'; readonly availableBytes: number; readonly photosRemaining: number }
  | { readonly state: 'full'; readonly availableBytes: number }

/**
 * Warn while roughly 25 photos still fit.
 *
 * Far enough ahead that the user can make a backup and delete deliberately —
 * which is the only way space is ever reclaimed, since D-081 forbids the app
 * from deleting anything itself.
 */
const LOW_STORAGE_BYTES = ESTIMATED_PHOTO_BYTES * 25

/**
 * Refuse below roughly two photos' worth.
 *
 * Not zero: the database needs room for the record, the indexes and its own
 * bookkeeping, and a write that fits the bytes but not the overhead fails just
 * as completely.
 */
const REFUSE_BELOW_BYTES = ESTIMATED_PHOTO_BYTES * 2

export async function checkPhotoQuota(): Promise<QuotaVerdict> {
  const estimate = await estimateStorage()
  if (!estimate.supported || estimate.availableBytes === undefined) return { state: 'unknown' }

  const availableBytes = estimate.availableBytes
  if (availableBytes < REFUSE_BELOW_BYTES) return { state: 'full', availableBytes }
  if (availableBytes < LOW_STORAGE_BYTES) {
    return {
      state: 'low',
      availableBytes,
      photosRemaining: Math.max(0, Math.floor(availableBytes / ESTIMATED_PHOTO_BYTES)),
    }
  }
  return { state: 'ok' }
}

/**
 * Process a captured file and store it as a photo.
 *
 * **The quota check runs first, before any decoding.** D-081 refuses a capture
 * it predicts cannot be written, and refusing after the work is done would burn
 * the memory and the moment for nothing.
 *
 * **The blobs are written before the record, and that order is deliberate.**
 * If a blob write fails the user gets a clean failure and no record exists. The
 * reverse order can leave a photo record pointing at bytes that were never
 * written — a broken image in a grid the user believes is their history.
 */
export async function savePhoto(
  db: SolaDatabase,
  input: SavePhotoInput,
): Promise<Result<PhotoRecord, SavePhotoError>> {
  const quota = await checkPhotoQuota()
  if (quota.state === 'full') {
    return err({
      kind: 'noRoom',
      message:
        'There is not enough room on this device to save another photo. Make a backup, then delete what you no longer need.',
    })
  }

  const processed = await processCapturedImage(input.file)
  if (!processed.ok) return err({ kind: 'pipeline', error: processed.error })

  const imageBlob = await putBlob(db, processed.value.image, processed.value.image.type)
  if (!imageBlob.ok) return err({ kind: 'storage', error: imageBlob.error })

  const thumbnailBlob = await putBlob(db, processed.value.thumbnail, processed.value.thumbnail.type)
  if (!thumbnailBlob.ok) return err({ kind: 'storage', error: thumbnailBlob.error })

  const capturedAt: IsoTimestamp = toIsoTimestamp(input.now)
  const capturedDate: CalendarDate = todayFor(input.now)

  const record: PhotoRecord = {
    id: newId<'PhotoId'>(),
    view: input.view,
    capturedDate,
    capturedAt,
    blobId: imageBlob.value,
    thumbnailBlobId: thumbnailBlob.value,
    widthPx: processed.value.size.width,
    heightPx: processed.value.size.height,
    byteSize: processed.value.image.size,
    source: input.source,
    ...(input.phaseId === undefined ? {} : { phaseId: input.phaseId }),
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    ...(input.note === undefined || input.note.trim() === '' ? {} : { note: input.note.trim() }),
    createdAt: capturedAt,
    updatedAt: capturedAt,
    schemaVersion: DB_SCHEMA_VERSION,
  }

  const written = await putPhoto(db, record)
  if (!written.ok) return err({ kind: 'storage', error: written.error })
  return ok(written.value)
}

export async function loadPhotos(
  db: SolaDatabase,
): Promise<Result<readonly PhotoRecord[], StorageError>> {
  return listPhotos(db)
}

/** Remove one photo. Always an explicit user decision (D-081). */
export async function removePhoto(
  db: SolaDatabase,
  id: PhotoId,
): Promise<Result<void, StorageError>> {
  return deletePhoto(db, id)
}

/** Remove every photo, leaving all other data untouched. */
export async function removeAllPhotos(db: SolaDatabase): Promise<Result<number, StorageError>> {
  return deleteAllPhotos(db)
}
